import logo from "../assets/logo.jpg";
import iWORKKZ from "../assets/iWORKKZ.png";
import cleanindia from "../assets/cleanindia.png";
import CityAutocomplete from "./CityAutocomplete";
import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function WebRegForm() {
  const navigate = useNavigate();
  const [cameraMode, setCameraMode] = useState("environment");
  // const [selectedEvent, setSelectedEvent] = useState("ev-001");
  const events = [{ id: "ev-001", label: "Clean India 2025 – Mumbai" }];

  const [capturedPhotos, setCapturedPhotos] = useState(Array(5).fill(null));
  const [activeSlot, setActiveSlot] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const CATEGORIES = [
    { key: "Cleaning", label: "Cleaning" },
    { key: "Delivery", label: "Delivery" },
    { key: "Greeting", label: "Greeting" },
    { key: "FS", label: "Facade/Solar" }, // key used in state
    { key: "iWorkkz", label: "iWorkkz" },
    { key: "All", label: "All" },
  ];

  const [categoryChecks, setCategoryChecks] = useState(
    CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: false }), {}),
  );

  const [purposeChecks, setPurposeChecks] = useState({
    demo: false,
    sales: false,
    genral: false,
  });

  const [city, setCity] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const API_BASE_URL = "https://prodapi.iworkkz.com:9095";
  const API_KEY = "X4gfacRza++DrL%2FqFYkhZJAuiuuJEsGwmfJz7tZHGi4%3D";

  const dataUrlToBase64 = (dataUrl) =>
    dataUrl ? (dataUrl.split(",")[1] ?? null) : null;

  // PURPOSE
  const onPurposeChange = (e) => {
    const { name, checked } = e.target;
    setPurposeChecks((prev) => ({ ...prev, [name]: checked }));
  };
  const PURPOSE_LABELS = { sales: "Sales", genral: "General", demo: "Demo" };

  // handler (no change required but kept for clarity)
  const onCategoryChange = (e) => {
    const { name, checked } = e.target;
    if (name === "All") {
      // when All toggled keep All explicit; other checkboxes stay disabled when All is true
      setCategoryChecks((prev) =>
        Object.keys(prev).reduce((acc, k) => {
          acc[k] = k === "All" ? checked : false;
          return acc;
        }, {}),
      );
    } else {
      setCategoryChecks((prev) => ({
        ...prev,
        [name]: checked,
        All: checked ? false : prev.All,
      }));
    }
  };

  // CAMERA
  const openCamera = async (slotIndex) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraMode },
      });
      streamRef.current = stream;
      setActiveSlot(slotIndex);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 100);
    } catch (err) {
      toast.error("Camera access denied or unavailable");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);

    const imageData = canvas.toDataURL("image/jpeg");

    setCapturedPhotos((prev) => {
      const updated = [...prev];
      updated[activeSlot] = imageData;
      return updated;
    });

    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCameraOpen(false);
    setActiveSlot(null);
  };

  const retakePhoto = (index) => {
    setCapturedPhotos((prev) => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });
  };

  // SUBMIT
  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // guard

    const form = e.currentTarget;
    const selectedPurposesArray = Object.entries(purposeChecks)
      .filter(([, v]) => v)
      .map(([k]) => PURPOSE_LABELS[k] || k); // readable labels

    // PURPOSE VALIDATION (mandatory)
    if (selectedPurposesArray.length === 0) {
      toast.error("Select at least one purpose.");
      return;
    }
    const selectedPurposesCsv = selectedPurposesArray.join(",");

    const phone = form.phone.value.trim();
    if (!/^\+91[0-9]{10}$/.test(phone)) {
      toast.error("Invalid mobile number. Use +911234567890 format.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.value.trim())) {
      toast.error("Invalid email address.");
      return;
    }

    if (!form.name.value.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!form.company.value.trim()) {
      toast.error("Company is required.");
      return;
    }
    if (!city.trim()) {
      toast.error("City is required.");
      return;
    }

    // CATEGORY VALIDATION (mandatory)
    const anyCategorySelected =
      categoryChecks.All ||
      Object.entries(categoryChecks).some(
        ([key, value]) => value === true && key !== "All",
      );

    if (!anyCategorySelected) {
      toast.error("Select at least one category.");
      return;
    }

    const categories = categoryChecks.All
      ? Object.keys(categoryChecks).filter((k) => k !== "All")
      : Object.entries(categoryChecks)
          .filter(([k, v]) => v && k !== "All")
          .map(([k]) => k);

    const payload = {
      // EventId: selectedEvent,
      EventId: "Website",
      Name: form.name.value.trim(),
      Company: form.company.value.trim(),
      City: city.trim(),
      Email: form.email.value.trim(),
      Phone: phone,
      Purpose: selectedPurposesCsv,
      Categories: categories.join(","),
      Message: form.message.value.trim(),
      CapturedPhotos: capturedPhotos
        .filter(Boolean)
        .map((p) => dataUrlToBase64(p)),
    };

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Enquiries?Key=${API_KEY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || data?.Error) {
        throw new Error(data?.Description || "Server Error");
      }

      toast.success(`Submitted successfully! Ref ID: ${data.Id || "N/A"}`);

      // reset
      form.reset();
      setCapturedPhotos(Array(5).fill(null));
      setCity("");
      setResetKey((k) => k + 1);
      setPurposeChecks({ demo: false, sales: false, genral: false });
      setCategoryChecks({
        Cleaning: false,
        Delivery: false,
        Greeting: false,
        FS: false,
        iWorkkz: false,
        All: false,
      });
    } catch (err) {
      toast.error(`Submit failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container py-4 robotics-bg">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-body p-4">
              <form onSubmit={onSubmit} noValidate>
                {/* disable native controls during submit */}
                <fieldset disabled={isSubmitting} aria-busy={isSubmitting}>
                  {/* NAME */}
                  <input
                    name="name"
                    type="text"
                    className="form-control mb-3"
                    placeholder="Your Name"
                    required
                  />

                  {/* COMPANY */}
                  <input
                    name="company"
                    type="text"
                    className="form-control mb-3"
                    placeholder="Your Company"
                    required
                  />

                  {/* CITY */}
                  <div className="mb-3">
                    <CityAutocomplete
                      key={resetKey}
                      name="city"
                      value={city}
                      onChange={(val) => setCity(val || "")}
                      onSelect={(opt) => setCity(opt?.label || opt || "")}
                    />
                  </div>

                  {/* EMAIL */}
                  <input
                    name="email"
                    type="email"
                    className="form-control mb-3"
                    placeholder="you@company.com"
                    required
                  />

                  {/* PHONE */}
                  <input
                    name="phone"
                    type="tel"
                    className="form-control mb-3"
                    defaultValue="+91"
                    onInput={(e) => {
                      let v = e.target.value.replace(/[^0-9+]/g, "");
                      if (!v.startsWith("+91")) v = "+91";
                      e.target.value = v.slice(0, 13);
                    }}
                  />

                  {/* PURPOSE */}
                  <label className="form-label d-block">Purpose</label>
                  <div className="row mb-3">
                    {[
                      { name: "sales", label: "Sales" },
                      { name: "genral", label: "General" },
                      { name: "demo", label: "Demo" },
                    ].map(({ name, label }) => {
                      const id = `chk-${name}`;
                      return (
                        <div className="col-4" key={name}>
                          <label
                            htmlFor={id}
                            className="form-check-label d-flex align-items-center"
                          >
                            <input
                              id={id}
                              className="form-check-input me-2"
                              type="checkbox"
                              name={name}
                              checked={purposeChecks[name]}
                              onChange={onPurposeChange}
                            />
                            {label}
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  {/* CATEGORY */}
                  <label className="form-label d-block">Category</label>
                  <div className="row mb-3">
                    {CATEGORIES.map((cat) => {
                      const id = `cat-${cat.key.replace(/\W+/g, "")}`;
                      return (
                        <div className="col-4" key={cat.key}>
                          <label
                            htmlFor={id}
                            className="form-check-label d-flex align-items-center"
                          >
                            <input
                              id={id}
                              className="form-check-input me-2"
                              type="checkbox"
                              name={cat.key}
                              checked={!!categoryChecks[cat.key]}
                              onChange={onCategoryChange}
                              disabled={categoryChecks.All && cat.key !== "All"}
                            />
                            {cat.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  {/* MESSAGE */}
                  <textarea
                    name="message"
                    className="form-control mb-3"
                    rows={2}
                    placeholder="Tell us about your needs..."
                    required
                  />

                  {/* SUBMIT */}
                  <button
                    type="submit"
                    className="btn btn-dark w-100 btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* CAMERA OVERLAY */}
      {cameraOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#000",
            zIndex: 9999,
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />

          {/* SWITCH CAMERA */}
          <button
            onClick={() => {
              streamRef.current?.getTracks().forEach((t) => t.stop());
              setCameraMode((prev) => {
                const next = prev === "environment" ? "user" : "environment";
                setTimeout(() => openCamera(activeSlot), 50);
                return next;
              });
            }}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.8)",
              border: "3px solid #ccc",
              fontSize: 24,
            }}
          >
            🔄
          </button>

          {/* CAPTURE BUTTON */}
          <button
            onClick={capturePhoto}
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: 40,
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "#fff",
              border: "4px solid #ddd",
            }}
          >
            📸
          </button>
        </div>
      )}

      {/* SUBMITTING OVERLAY */}
      {isSubmitting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="spinner-border" role="status" aria-hidden="true" />
          <div>Submitting... Please wait</div>
        </div>
      )}
    </div>
  );
}
